import { useEffect, useState } from "react";
import { Loader2, Mail, Paperclip, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const statusLabel = { smtp: "Wysłano (home.pl)", proxy: "Wysłano (kanał zapasowy)" };

export function EmailArchiveAdmin() {
  const [items, setItems] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = () => api.get("/admin/email-archive").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    setLoadingPreview(true);
    try { const r = await api.get(`/admin/email-archive/${id}`); setPreview(r.data); }
    catch { toast.error("Nie udało się wczytać wiadomości."); }
    finally { setLoadingPreview(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Usunąć tę wiadomość z archiwum?")) return;
    try { await api.delete(`/admin/email-archive/${id}`); toast.success("Usunięto z archiwum."); if (preview?.id === id) setPreview(null); load(); }
    catch { toast.error("Błąd usuwania."); }
  };

  return (
    <div data-testid="email-archive-admin" className="space-y-5">
      <div className="bg-card border border-border rounded-2xl">
        <div className="p-6 pb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg">Archiwum wysłanych e-maili</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
              Kopie wiadomości wysłanych do klientów (potwierdzenia, przypomnienia, dokumenty) — podgląd bez wchodzenia do skrzynki.
              Zgodnie z polityką RODO wpisy są przechowywane <strong>24 godziny</strong> (bez plików PDF — tylko treść) i znikają automatycznie.
            </p>
          </div>
          <button data-testid="email-archive-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted">
            <RefreshCw size={13} /> Odśwież
          </button>
        </div>
        <div className="divide-y divide-border border-t border-border">
          {!items && <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>}
          {items && !items.length && <p data-testid="email-archive-empty" className="p-6 text-sm text-muted-foreground">Brak zarchiwizowanych wiadomości z ostatnich 24 h.</p>}
          {items?.map((it) => (
            <div key={it.id} data-testid={`email-archive-${it.id}`} className="p-4 sm:px-6 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <p className="font-semibold text-sm flex items-center gap-2"><Mail size={14} className="text-brand-teal shrink-0" />{it.subject}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  do: <span className="font-mono">{it.to}</span> · {new Date(it.created_at).toLocaleString("pl-PL")} · {statusLabel[it.status] || it.status}
                  {it.attachments?.length ? <span className="inline-flex items-center gap-1 ml-1"><Paperclip size={11} />{it.attachments.length} zał.</span> : null}
                </p>
              </div>
              <button data-testid={`email-archive-view-${it.id}`} onClick={() => open(it.id)} className="text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted">Podgląd</button>
              <button data-testid={`email-archive-delete-${it.id}`} onClick={() => remove(it.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red border border-brand-red/30 rounded-full px-3 py-1.5 hover:bg-brand-red/10"><Trash2 size={13} /> Usuń</button>
            </div>
          ))}
        </div>
      </div>

      {(preview || loadingPreview) && (
        <div data-testid="email-archive-preview" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{preview?.subject || "Wczytywanie…"}</p>
                {preview && <p className="text-xs text-muted-foreground truncate">do: {preview.to} · {new Date(preview.created_at).toLocaleString("pl-PL")}</p>}
              </div>
              <button data-testid="email-archive-preview-close" onClick={() => setPreview(null)} className="p-1.5 rounded-full hover:bg-muted"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50">
              {loadingPreview && <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>}
              {preview && <iframe title="Podgląd e-maila" srcDoc={preview.html} className="w-full h-[70vh] bg-white border-0" sandbox="" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
